import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mic, Send, Sparkles, MoreHorizontal, Search, Paperclip, Cpu, Check } from 'lucide-react';
import { chatContent, DEFAULT_CHAT, routeDisplayNames } from './chat-content';
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: number;
}
interface ModelOption {
  id: string;
  label: string;
  description: string;
}
const MODELS: ModelOption[] = [{
  id: 'claude-sonnet-4-6',
  label: 'Claude Sonnet 4.6',
  description: 'Default — balanced speed and quality'
}, {
  id: 'claude-haiku-4-5',
  label: 'Claude Haiku 4.5',
  description: 'Fastest, lower cost'
}, {
  id: 'claude-opus-4-6',
  label: 'Claude Opus 4.6',
  description: 'Highest quality, slower'
}, {
  id: 'gpt-4o-mini-search',
  label: 'GPT-4o-mini Search',
  description: 'Web search built in'
}];
interface Props {
  isOpen: boolean;
  onClose: () => void;
  route: string;
  isMobile: boolean;
}
export function AIChatPanel({
  isOpen,
  onClose,
  route,
  isMobile
}: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [listening, setListening] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [currentModel, setCurrentModel] = useState<string>('claude-sonnet-4-6');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (modelModalOpen) {
          setModelModalOpen(false);
          return;
        }
        if (menuOpen) {
          setMenuOpen(false);
          return;
        }
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, menuOpen, modelModalOpen]);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages]);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);
  const entry = chatContent[route] ?? DEFAULT_CHAT;
  const routeLabel = routeDisplayNames[route] ?? route;
  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    const userMsg: ChatMessage = {
      id: `${Date.now()}-u`,
      role: 'user',
      text,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setTimeout(() => {
      const reply: ChatMessage = {
        id: `${Date.now()}-a`,
        role: 'assistant',
        text: 'Got it — let me take a look.',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, reply]);
    }, 1000);
  };
  const handleMicClick = () => {
    setListening(true);
    setTimeout(() => setListening(false), 2000);
  };
  const handleStarterPrompt = (prompt: string) => {
    setInput(prompt);
    textareaRef.current?.focus();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const handleUploadClick = () => {
    setMenuOpen(false);
    fileInputRef.current?.click();
  };
  const handleModelClick = () => {
    setMenuOpen(false);
    setModelModalOpen(true);
  };
  const handleSearchToggle = () => {
    setSearchEnabled(prev => !prev);
    setMenuOpen(false);
  };
  const body = <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-[#141418]" data-id="element-113">
        <div className="flex items-center gap-2" data-id="element-114">
          <div className="w-7 h-7 rounded-[10px] bg-[#4a9a9a]/15 flex items-center justify-center" data-id="element-115">
            <Sparkles size={14} className="text-[#4a9a9a]" data-id="element-116" />
          </div>
          <div data-id="element-117">
            <p className="text-[14px] font-medium text-[#e8e4df] leading-tight" data-id="element-118">ZenBot</p>
            <p className="text-[11px] text-[#8a8680] leading-tight" data-id="element-119">on {routeLabel}</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors" aria-label="Close chat" data-id="element-120">
          <X size={18} className="text-[#8a8680]" data-id="element-121" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3" data-id="element-122">
        {messages.length === 0 ? <>
            <div className="bg-[#252530] text-[#e8e4df] rounded-[12px] px-3 py-2 max-w-[85%] text-[14px] leading-relaxed" data-id="element-123">
              {entry.opener}
            </div>
            <div className="flex flex-wrap gap-2 mt-2" data-id="element-124">
              {entry.starterPrompts.map(prompt => <button key={prompt} onClick={() => handleStarterPrompt(prompt)} className="text-[12px] bg-white/[0.04] text-[#8a8680] hover:text-[#e8e4df] hover:bg-white/[0.06] px-3 py-1 rounded-full transition-colors" data-id="element-125">
                  {prompt}
                </button>)}
            </div>
          </> : messages.map(msg => <div key={msg.id} className={`rounded-[12px] px-3 py-2 max-w-[85%] text-[14px] leading-relaxed ${msg.role === 'user' ? 'bg-[#4a9a9a]/20 text-[#e8e4df] self-end' : 'bg-[#252530] text-[#e8e4df]'}`} data-id="element-126">
              {msg.text}
            </div>)}
      </div>

      <div className="border-t border-white/[0.06] px-3 pt-3 pb-[max(0.5rem,env(safe-area-inset-bottom))]" data-id="element-127">
        <div className="flex items-end gap-2" data-id="element-128">
          <div className="relative flex-1 min-w-0" data-id="element-129">
            <textarea ref={textareaRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder={listening ? 'Listening…' : 'Ask anything…'} className="w-full bg-[#252530] text-[#e8e4df] rounded-[12px] pl-3 pr-14 py-2 text-[14px] outline-none resize-none min-h-[48px] max-h-[120px] placeholder:text-[#8a8680]/50" rows={1} data-id="element-130" />
            <button onClick={e => {
            e.stopPropagation();
            handleMicClick();
          }} className={`absolute right-1.5 bottom-1.5 w-9 h-9 rounded-[10px] flex items-center justify-center transition-colors ${listening ? 'bg-[#4a9a9a]/25 text-[#4a9a9a]' : 'bg-[#2a2a35] text-[#8a8680] hover:bg-[#353540] hover:text-[#e8e4df]'}`} aria-label="Voice input" data-id="element-131">
              <Mic size={14} data-id="element-132" />
            </button>
          </div>

          <div className="relative shrink-0" data-id="element-133">
            <button onClick={() => setMenuOpen(prev => !prev)} className={`w-10 h-10 rounded-[12px] flex items-center justify-center transition-colors ${menuOpen ? 'bg-[#2a2a35] text-[#e8e4df]' : 'bg-[#252530] text-[#8a8680] hover:bg-[#2a2a35] hover:text-[#e8e4df]'}`} aria-label="More options" aria-expanded={menuOpen} data-id="element-134">
              <MoreHorizontal size={16} data-id="element-135" />
            </button>
            {menuOpen && <>
                <div className="fixed inset-0 z-[55]" onClick={() => setMenuOpen(false)} data-id="element-136" />
                <div className="absolute bottom-12 right-0 z-[60] w-52 bg-[#1c1c22] rounded-[12px] shadow-xl shadow-black/40 border border-white/[0.06] overflow-hidden" data-id="element-137">
                  <button onClick={handleSearchToggle} className="w-full flex items-center justify-between px-3 py-2.5 text-[13px] text-left hover:bg-white/[0.04] transition-colors" data-id="element-138">
                    <span className="flex items-center gap-2 text-[#e8e4df]" data-id="element-139">
                      <Search size={14} className="text-[#8a8680]" data-id="element-140" />
                      Search
                    </span>
                    {searchEnabled && <Check size={14} className="text-[#4a9a9a]" data-id="element-141" />}
                  </button>
                  <button onClick={handleUploadClick} className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-left text-[#e8e4df] hover:bg-white/[0.04] transition-colors" data-id="element-142">
                    <Paperclip size={14} className="text-[#8a8680]" data-id="element-143" />
                    Upload file
                  </button>
                  <button onClick={handleModelClick} className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-left text-[#e8e4df] hover:bg-white/[0.04] transition-colors" data-id="element-144">
                    <Cpu size={14} className="text-[#8a8680]" data-id="element-145" />
                    Switch model
                  </button>
                </div>
              </>}
          </div>

          <button onClick={handleSend} disabled={!input.trim()} className="w-10 h-10 rounded-[12px] bg-[#4a9a9a] text-[#141418] flex items-center justify-center hover:bg-[#5bb5b5] transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Send message" data-id="element-146">
            <Send size={16} data-id="element-147" />
          </button>
        </div>

        <div className="flex items-center justify-between mt-1.5 px-1" data-id="element-148">
          <button onClick={() => setModelModalOpen(true)} className="text-[11px] text-[#8a8680]/60 hover:text-[#8a8680] transition-colors py-1.5 -my-1.5 px-1 -mx-1" data-id="element-149">
            {currentModel}
          </button>
          {searchEnabled && <span className="text-[11px] text-[#4a9a9a]/70" data-id="element-150">search on</span>}
        </div>

        <input ref={fileInputRef} type="file" className="hidden" data-id="element-151" />
      </div>

      {modelModalOpen && <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={() => setModelModalOpen(false)} data-id="element-152">
          <div className="bg-[#1c1c22] rounded-[16px] w-full max-w-[380px] p-5 border border-white/[0.06] shadow-2xl shadow-black/40" onClick={e => e.stopPropagation()} data-id="element-153">
            <div className="flex items-center justify-between mb-4" data-id="element-154">
              <h3 className="text-[15px] font-medium text-[#e8e4df]" data-id="element-155">Model</h3>
              <button onClick={() => setModelModalOpen(false)} className="w-7 h-7 rounded-lg hover:bg-white/5 flex items-center justify-center transition-colors" aria-label="Close model picker" data-id="element-156">
                <X size={16} className="text-[#8a8680]" data-id="element-157" />
              </button>
            </div>
            <div className="flex flex-col gap-2" data-id="element-158">
              {MODELS.map(m => {
            const selected = m.id === currentModel;
            return <button key={m.id} onClick={() => {
              setCurrentModel(m.id);
              setModelModalOpen(false);
            }} className={`w-full text-left rounded-[12px] p-3 border transition-colors ${selected ? 'border-[#4a9a9a]/50 bg-[#4a9a9a]/10' : 'border-white/[0.06] bg-[#252530] hover:bg-[#2a2a35]'}`} data-id="element-159">
                    <div className="flex items-center justify-between" data-id="element-160">
                      <span className={`text-[14px] font-medium ${selected ? 'text-[#4a9a9a]' : 'text-[#e8e4df]'}`} data-id="element-161">
                        {m.label}
                      </span>
                      {selected && <Check size={14} className="text-[#4a9a9a]" data-id="element-162" />}
                    </div>
                    <p className="text-[12px] text-[#8a8680] mt-0.5" data-id="element-163">{m.description}</p>
                  </button>;
          })}
            </div>
          </div>
        </div>}
    </>;
  if (isMobile) {
    return <AnimatePresence data-id="element-164">
        {isOpen && <motion.div key="chat-panel-mobile-backdrop" initial={{
        opacity: 0,
        pointerEvents: 'auto' as const
      }} animate={{
        opacity: 1,
        pointerEvents: 'auto' as const
      }} exit={{
        opacity: 0,
        pointerEvents: 'none' as const
      }} transition={{
        duration: 0.2
      }} className="fixed inset-x-0 bottom-0 top-14 z-50 bg-black/60 flex items-end" onClick={onClose} data-id="element-165">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 34
        }} className="bg-[#1a1a20] w-full h-[calc(100dvh-56px-48px)] rounded-t-[20px] border-t border-x border-white/[0.06] flex flex-col relative overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-166">
              {body}
            </motion.div>
          </motion.div>}
      </AnimatePresence>;
  }
  return <AnimatePresence data-id="element-167">
      {isOpen && <motion.aside key="chat-panel-desktop" initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed top-14 right-0 bottom-0 w-[400px] bg-[#1a1a20] border-l border-white/[0.06] z-20 flex flex-col overflow-hidden" data-id="element-168">
          {body}
        </motion.aside>}
    </AnimatePresence>;
}